import type { FieldValues } from 'react-hook-form';

/**
 * Per-key dirty markers as produced by react-hook-form's
 * `formState.dirtyFields`: a changed leaf is `true`, and a nested object mirrors
 * the value's shape.
 */
type DirtyFieldMarks<T> = {
  [K in keyof T]?: DirtyFieldMarks<NonNullable<T[K]>> | boolean;
};

/**
 * Pick only the fields react-hook-form marked dirty from `values`, preserving
 * nested object shape, so a PATCH body carries just what the user changed.
 */
export const pickDirtyValues = <T extends FieldValues>(
  dirtyFields: DirtyFieldMarks<T>,
  values: T,
): Partial<T> => {
  const result: Partial<T> = {};

  const dirtyKeys = (Object.keys(dirtyFields) as (keyof T)[]).filter(
    key => dirtyFields[key],
  );

  for (const key of dirtyKeys) {
    const mark = dirtyFields[key];

    // A changed leaf is marked `true`; a nested object mirrors the value's
    // shape, so recurse to keep only its changed sub-fields.
    result[key] =
      mark === true
        ? values[key]
        : (pickDirtyValues(
            mark as DirtyFieldMarks<FieldValues>,
            values[key] as FieldValues,
          ) as T[keyof T]);
  }

  return result;
};
